import React, { useMemo } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  Button,
  Stack,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
// import Hero from '../components/home/Hero';
import { getImageUrl } from '../utils/helpers';
import useActivityTypes from '../hooks/useActivityTypes';

/** Legacy hardcoded catalogue — used only if API types are unavailable. */
const FALLBACK_ACTIVITY_CATEGORIES = [
  {
    id: 'soin',
    title: 'Massage Intuitif',
    filterName: 'Soin',
    description: "Le massage intuitif appréhende le toucher comme un art et le place au centre de la relation, il vise à mettre en lien émotions, sentiments, ressentis et sensations, dans une intégration commune.",
    image: 'soin_ameliebenzino.jpeg',
    longDescription: ''
  },
  {
    id: 'yoga-ashtanga',
    title: 'Yoga Ashtanga',
    filterName: 'Yoga Ashtanga',
    description: "Le yoga Ashtanga est un yoga très rigoureux et dynamique. Il se compose d'une série de posture qui se pratique toujours dans le même ordre, c'est pourquoi le yoga Ashtanga peut être bénéfique pour les débutants du yoga car il permet d'acquérir les positions et la série avant de se lancer, contrairement au Vinyasa yoga ou l'on ne connaît pas l'enchaînement.",
    image: 'ashtanga_ameliebenzino.jpg',
    longDescription: ''
  },
  {
    id: 'hatha-yoga',
    title: 'Hatha Yoga',
    filterName: 'Hatha Yoga',
    description: "Le hatha yoga est une pratique douce qui associe postures, respiration et relaxation pour renforcer le corps, assouplir et apaiser le mental.",
    image: 'YogaEnergies2.jpeg',
    longDescription: ''
  },
  {
    id: 'yoga-vinyasa',
    title: 'Yoga Vinyasa',
    filterName: 'Yoga Vinyasa',
    description: "Lors de mon cours de Vinyasa c'est également ma personnalité qui s'exprime à travers des thèmes que je propose chaque semaine. Nous verrons des thèmes émotionnels (lâcher prise, no stress etc) et des thèmes physiques (yoga pour les genoux, ouverture des hanches etc) mais j'aime aussi suivre les saisons (début d'été, fin d'hiver etc). Il est complémentaire au cours d'Ashtanga qui est plus rigoureux et qui suit toujours le même schéma.",
    image: 'vinyasa_ameliebenzino.jpg',
    longDescription: ''
  },
  {
    id: 'yoga-restauratif',
    title: 'Yoga Restauratif',
    filterName: 'Yoga Restauratif',
    description: "Le cours de yoga restauratif est un cours de yoga basé sur des postures de relaxation accompagné de pranayama (technique de respiration)",
    image: 'restauratif_ameliebenzino.jpg',
    longDescription: ''
  },
  {
    id: 'yin-yoga',
    title: 'Yin Yoga',
    filterName: 'Yin Yoga',
    description: "Le yin yoga est grandement influencé par les philosophies asiatiques, le Taoïsme et la médecine traditionnelle chinoise. Cela étant, la médecine conventionnelle comme nous la connaissons en France reconnaît l'importance cruciale d'activités telles que le yin, qui lient physique et psychique.",
    image: 'yinyoga_ameliebenzino.jpg',
    longDescription: ''
  },
  {
    id: 'shiatsu',
    title: 'Shiatsu',
    filterName: 'Shiatsu',
    description: "Le shiatsu est une thérapie manuelle d'origine japonaise qui utilise des pressions, étirements et mobilisations pour équilibrer l'énergie vitale du corps. Inspirée de la Médecine Traditionnelle Chinoise, cette pratique douce favorise la relaxation, soulage les tensions et renforce le bien-être global.",
    image: 'shiatsu_ameliebenzino.jpg',
    longDescription: ''
  },
  {
    id: 'chi-nei-tsang',
    title: 'Chi Nei Tsang',
    filterName: 'Chi Nei Tsang',
    description: "Le Chi Nei Tsang est une technique de massage abdominal issue de la tradition taoïste. En agissant sur le ventre, considéré comme le centre énergétique du corps, il aide à libérer les tensions, améliorer la digestion et équilibrer les émotions pour un bien-être profond.",
    image: 'cnt_ameliebenzino.jpg',
    longDescription: ''
  },
  {
    id: 'pilates-tous-niveaux',
    title: 'Pilates - Tous niveaux',
    filterName: 'Pilates - Tous niveaux',
    description: "Une pratique où la force naît de la douceur : chaque mouvement devient une exploration du corps, du souffle et de la présence.",
    image: 'Pilates2.jpeg',
    longDescription: ''
  },
  {
    id: 'ateliers',
    title: 'Ateliers',
    filterName: 'Ateliers',
    description: 'Des sessions spéciales pour approfondir votre pratique et explorer de nouvelles dimensions.',
    image: 'ateliers.jpeg',
    longDescription: ''
  }
];

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { types, ready } = useActivityTypes({ homeOnly: true });

  const activityCategories = useMemo(() => {
    if (!ready) return FALLBACK_ACTIVITY_CATEGORIES;
    return types.map((type) => ({
      id: type.slug || type._id,
      title: type.displayName || type.name,
      filterName: type.name,
      description: type.shortDescription || '',
      image: type.image || '',
      longDescription: type.longDescription || type.shortDescription || ''
    }));
  }, [ready, types]);

  const handleCategoryClick = (category) => {
    const filterCategory = category.filterName || category.title;
    navigate(`/activities/category-info/${encodeURIComponent(filterCategory)}`, {
      state: {
        category: category.title,
        shortDescription: category.description,
        longDescription: category.longDescription || category.description,
        image: category.image,
        filterCategory
      }
    });
  };

  const handleViewSchedule = (e, category) => {
    e.stopPropagation();
    const categoryTitle = category.filterName || category.title;
    navigate(`/activities/category/${encodeURIComponent(categoryTitle)}`);
  };

  return (
    <>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        {/* Activities Catalog Section */}
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
          <Typography
            variant="h2"
            align="center"
            sx={{ 
              mb: { xs: 4, md: 6 },
              fontWeight: 700,
              fontSize: {
                xs: '2rem',
                sm: '2.5rem',
                md: '3rem'
              }
            }}
          >
            {t('discoverActivities')}
          </Typography>

          {/* Activities Grid */}
          <Grid container spacing={4}>
            {activityCategories.map((category) => (
              <Grid item xs={12} sm={6} md={4} key={category.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    },
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                  }}
                >
                  <CardActionArea 
                    onClick={() => handleCategoryClick(category)}
                    sx={{ height: '100%' }}
                  >
                    <CardMedia
                      component="img"
                      height="300"
                      image={getImageUrl(category.image)}
                      alt={category.title}
                      sx={{
                        objectFit: 'cover',
                      }}
                    />
                    <CardContent sx={{ flexGrow: 1, p: 3, bgcolor: 'background.paper' }}>
                      <Stack spacing={2}>
                        <Typography
                          variant="h5"
                          component="h2"
                          sx={{
                            fontWeight: 700,
                            color: 'primary.main',
                          }}
                        >
                          {category.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.6,
                          }}
                        >
                          {category.description}
                        </Typography>
                        <Button
                          variant="text"
                          color="primary"
                          endIcon={<CalendarMonthIcon />}
                          onClick={(e) => handleViewSchedule(e, category)}
                          sx={{
                            alignSelf: 'flex-start',
                            mt: 'auto',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          {t('viewSchedule')}
                        </Button>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Join Community Section */}
        <Box
          sx={{
            py: { xs: 6, sm: 8, md: 12 },
            textAlign: 'center',
            backgroundColor: 'secondary.main',
            color: 'secondary.contrastText',
            mt: 8,
            borderRadius: 2,
          }}
        >
          <Container maxWidth="md">
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                color: 'common.white',
                fontWeight: 700,
                mb: 3,
                fontSize: {
                  xs: '1.75rem',
                  sm: '2.25rem',
                  md: '2.75rem'
                },
                lineHeight: 1.3
              }}
            >
              {t('readyToJoin')}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'common.white',
                opacity: 0.8,
                mb: 4,
                mx: 'auto',
                maxWidth: 600,
                fontSize: {
                  xs: '1rem',
                  sm: '1.1rem',
                  md: '1.25rem'
                },
                lineHeight: 1.6
              }}
            >
              {t('joinCommunityMessage')}
            </Typography>
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              size="large"
              sx={{
                minWidth: { xs: 160, sm: 200 },
                fontSize: { xs: '0.9rem', sm: '1rem' },
                py: { xs: 1, sm: 1.5 },
                px: { xs: 2, sm: 3 },
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              {t('getStarted')}
            </Button>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Home; 